/**
 * Shared grounded-send helper for G.5/G.6 (2026-05-27).
 *
 * Routes a message through the strict-grounded `/api/studio-plaz/grounded-answer`
 * endpoint and normalises the response into the shape expected by chat
 * components (`{ content, refused, refusal_reason, confidence, citations,
 * reasoning_boundary, top_score, run_id }`).
 *
 * Self-disables on 401/403 — non-super-admin surfaces can opt in safely
 * and gracefully fall back to their existing chat path.
 */
import { useState, useCallback } from "react";
import { api } from "./api";

export function useGroundedSend({ surface, conversationId } = {}) {
  const [disabled, setDisabled] = useState(false);

  const groundedSend = useCallback(
    async (message, opts = {}) => {
      if (disabled) return { fallback: true, reason: "disabled" };
      try {
        const r = await api.post("/studio-plaz/grounded-answer", {
          q: message,
          top_k: opts.top_k ?? 5,
          refuse_below_score: opts.refuse_below_score ?? 0.08,
          surface: surface || "chat",
          page_id: conversationId || opts.page_id || null,
        });
        const d = r.data;
        return {
          ok: true,
          run_id: d.run_id,
          refused: !!d.refused,
          refusal_reason: d.refusal_reason,
          content: d.refused
            ? d.refusal_reason || "Insufficient grounding to answer."
            : d.answer,
          confidence: d.confidence,
          citations: d.citations || [],
          reasoning_boundary: d.reasoning_boundary,
          top_score: d.retrieval?.top_score,
        };
      } catch (e) {
        // Self-disable on auth failure — tenant surfaces never see broken affordance
        if ([401, 403].includes(e?.response?.status)) {
          setDisabled(true);
          return { fallback: true, reason: "unauthorized" };
        }
        return {
          fallback: false,
          ok: false,
          error: e?.response?.data?.detail || e.message || "Grounding call failed.",
        };
      }
    },
    [disabled, surface, conversationId],
  );

  return { groundedSend, groundedDisabled: disabled };
}
